import { Subject, Subscription, debounceTime, takeUntil } from 'rxjs';

import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';

import {
  ErrorMessageObject,
  FieldValidationErrorObject,
} from '../interfaces/form-error.interface';
import {
  getControlName,
  getControlOwnErrors,
  getFieldValidationErrors,
  getFormValidationErrors,
} from './form-errors-helper';
import {
  findMatchingConditionalControl,
  processNestedControls,
  setValueToControl,
} from './form-helpers';

/**
 * Represents a single conditional control option
 * @description
 * Defines a condition for creating a dynamic form control based on data key present in the input
 */
export interface ConditionalControlOption<
  TConditional extends Record<string, any> = Record<string, any>,
  TControl extends AbstractControl = AbstractControl,
> {
  /** Key to check in the patched data */
  key: Extract<keyof TConditional, string> | string;
  /** Factory function to create a new control when a key is found */
  factory: (value?: any) => TControl;
}

/**
 * Represents a set of conditional controls that will be considered together
 * @description
 * A group of conditional control options where only one will be active at a time
 */
export interface ConditionalControlSet<
  TConditional extends Record<string, any> = Record<string, any>,
> {
  /** Multiple conditions to check, first matching one will be used */
  conditions: ReadonlyArray<ConditionalControlOption<TConditional>>;
  /** When true, prevents creating default controls when no matching condition is found */
  skipDefault?: boolean;
}

/**
 * Configuration options for CustomFormGroup
 * @description
 * Options for configuring the CustomFormGroup including error handling and conditional controls
 */
export interface CustomFormGroupOptions<
  TConditional extends Record<string, any> = Record<string, any>,
> {
  /** Error messages for validation, maps error types to display messages */
  errorMessages?: ReadonlyArray<ErrorMessageObject>;
  /** Conditional controls that will be added dynamically based on input data */
  conditionalControls?: ReadonlyArray<ConditionalControlSet<TConditional>>;
}

export interface UpdateOptions {
  onlySelf?: boolean;
  emitEvent?: boolean;
}

export type ValidatorOrOpts =
  ValidatorFn | AbstractControlOptions | ValidatorFn[] | null;

export type AsyncValidatorOrOpts = AsyncValidatorFn | AsyncValidatorFn[] | null;

/**
 * CustomFormGroup extends Angular's FormGroup with advanced conditional control handling and error management.
 *
 * @description
 * This custom form group component supports:
 * - Dynamic creation of controls based on data patterns
 * - Conditional form structures that adapt to input data
 * - Intelligent error message handling and aggregation
 * - Advanced patching behavior for complex nested forms
 *
 * Key features and edge cases:
 * 1. Controls can be added/removed dynamically based on input data patterns
 * 2. Error messages are aggregated from nested forms and can be customized
 * 3. Handles deeply nested form structures with proper cleanup
 * 4. Supports conditional controls with fallback/default options
 */
export class CustomFormGroup<
  TStatic extends {
    [K in keyof TStatic]: AbstractControl<any, any, any>;
  } = any,
  TConditional extends Record<string, any> = any,
> extends FormGroup<TStatic & Partial<TConditional>> {
  public errorMessages: ReadonlyArray<ErrorMessageObject> | undefined;
  public conditionalControls:
    ReadonlyArray<ConditionalControlSet<TConditional>> | undefined;
  public allErrors: FieldValidationErrorObject[] = [];

  private readonly destroyed$ = new Subject<void>();
  private valueChangesSubscription: Subscription | null = null;
  /**
   * Time in milliseconds to debounce the valueChanges events
   * This helps reduce the number of error calculations when values change rapidly
   */
  private readonly DEBOUNCE_TIME = 100;

  /**
   * Creates a new CustomFormGroup instance
   *
   * @param controls The initial set of controls to include in this form group
   * @param options Custom options including error messages and conditional controls
   * @param validatorOrOpts Angular validators or options for this form group
   * @param asyncValidator Angular async validators for this form group
   *
   * @example
   * ```typescript
   * // Basic form group
   * const userForm = new CustomFormGroup({
   *   name: new FormControl(''),
   *   email: new FormControl('')
   * });
   *
   * // With error messages
   * const userForm = new CustomFormGroup(
   *   { name: new FormControl('') },
   *   {
   *     errorMessages: [
   *       {
   *         fieldKey: 'name',
   *         errorName: 'required',
   *         message: 'Name is required'
   *       }
   *     ]
   *   }
   * );
   *
   * // With conditional controls
   * const addressForm = new CustomFormGroup(
   *   { type: new FormControl('') },
   *   {
   *     conditionalControls: [
   *       {
   *         conditions: [
   *           {
   *             key: 'domestic',
   *             factory: () => new FormGroup({
   *               state: new FormControl(''),
   *               zipCode: new FormControl('')
   *             })
   *           },
   *           {
   *             key: 'international',
   *             factory: () => new FormGroup({
   *               country: new FormControl(''),
   *               postalCode: new FormControl('')
   *             })
   *           }
   *         ]
   *       }
   *     ]
   *   }
   * );
   * ```
   */
  constructor(
    controls: TStatic,
    options?: CustomFormGroupOptions<TConditional>,
    validatorOrOpts?: ValidatorOrOpts,
    asyncValidator?: AsyncValidatorOrOpts
  ) {
    super(
      controls as TStatic & Partial<TConditional>,
      validatorOrOpts,
      asyncValidator
    );

    if (options) {
      this.errorMessages = options.errorMessages;
      this.conditionalControls = options.conditionalControls;
    }

    this.setupValueChangesSubscription();
  }

  /**
   * Sets up the valueChanges subscription with debouncing to improve performance
   */
  private setupValueChangesSubscription(): void {
    this.cleanupValueChangesSubscription();

    if (this.errorMessages) {
      this.valueChangesSubscription = this.valueChanges
        .pipe(debounceTime(this.DEBOUNCE_TIME), takeUntil(this.destroyed$))
        .subscribe(() => {
          this.allErrors = this.getAllErrors();
        });
    }
  }

  /**
   * Cleanup method for valueChanges subscription
   */
  private cleanupValueChangesSubscription(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
      this.valueChangesSubscription = null;
    }
  }

  /**
   * Cleanup all subscriptions when the form group is no longer needed
   * This should be called by components using this form when they are destroyed
   *
   * @description
   * CustomFormGroup creates internal subscriptions that need to be properly cleaned up
   * to prevent memory leaks. This method handles that cleanup and should be called
   * in the ngOnDestroy lifecycle hook of any component using CustomFormGroup.
   *
   * @example
   * ```typescript
   * export class MyFormComponent implements OnInit, OnDestroy {
   *   myForm = new CustomFormGroup({...});
   *
   *   ngOnInit() {
   *     // Form setup
   *   }
   *
   *   ngOnDestroy() {
   *     // Important! Always call destroy to prevent memory leaks
   *     this.myForm.destroy();
   *   }
   * }
   * ```
   */
  public destroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.cleanupValueChangesSubscription();
  }

  /**
   * Improved patchValue method to handle conditional controls and process nested forms
   *
   * @description
   * This enhanced patchValue implementation adds several important features:
   * - Creates/updates conditional controls based on input data patterns
   * - Processes CustomFormControl instances with multi-key handling
   * - Handles nested CustomFormGroups and CustomFormArrays properly
   * - Resets any controls that don't have matching values in the data
   *
   * @param value Object containing the values to patch
   * @param options Standard Angular form options
   *
   * @example
   * ```typescript
   * // With conditional controls
   * const form = new CustomFormGroup(
   *   { type: new FormControl('') },
   *   {
   *     conditionalControls: [
   *       {
   *         conditions: [
   *           {
   *             key: 'domestic',
   *             factory: () => new FormGroup({...})
   *           },
   *           {
   *             key: 'international',
   *             factory: () => new FormGroup({...})
   *           }
   *         ]
   *       }
   *     ]
   *   }
   * );
   *
   * // Will create the domestic address FormGroup
   * form.customPatchValue({
   *   type: 'US',
   *   domestic: { state: 'CA', zipCode: '90210' }
   * });
   *
   * // Will switch to international address FormGroup
   * form.customPatchValue({
   *   type: 'International',
   *   international: { country: 'Canada', postalCode: 'A1B 2C3' }
   * });
   * ```
   */
  customPatchValue(
    value: Partial<{ [K in keyof (TStatic & TConditional)]: any }>,
    options: UpdateOptions = {}
  ): void {
    if (!value) {
      return;
    }

    // Create a shared map for tracking processed controls
    const processedControlsMap = new Map<AbstractControl, boolean>();

    // Process all controls, including custom ones
    processNestedControls(this, value, options, processedControlsMap);

    this.patchValue(value, options);
  }

  /**
   * Processes conditional controls by checking the value object for matching keys
   * Creates the appropriate controls based on matching conditions, or default controls if no match is found
   * and skipDefault is not set to true
   *
   * @description
   * This method is the core of CustomFormGroup's dynamic control handling. It examines
   * the input data structure and creates, removes, or updates controls based on
   * the conditional control configurations.
   * Used by the customPatchValue method to ensure conditional controls are handled
   *
   * @param value The value object to check for matching keys
   * ```
   */
  processConditionalControls(value: { [key: string]: any }): void {
    if (!this.conditionalControls) {
      return;
    }

    for (const controlSet of this.conditionalControls) {
      this.processConditionalControlSet(controlSet, value);
    }
  }

  /**
   * Processes a single conditional control set
   * @param controlSet The conditional control set to process
   * @param value The value object to check against
   * @param options Form options to propagate to created controls
   */
  private processConditionalControlSet(
    controlSet: ConditionalControlSet,
    value: { [key: string]: any }
  ): void {
    // Find a matching condition based on the keys in the value object
    const result = findMatchingConditionalControl(controlSet, value);
    const {
      option: matchingOption,
      key: matchingKey,
      foundValue: matchingValue,
    } = result;

    // No conditions to process
    if (controlSet.conditions.length === 0) {
      return;
    }

    try {
      if (matchingOption && matchingKey) {
        // Match found - use the matching option
        this.handleMatchingCondition(
          controlSet,
          matchingOption,

          matchingValue
        );
      } else if (controlSet.skipDefault) {
        // No match found and skipDefault is true - remove any existing conditional controls
        this.removeConditionalControls(controlSet);
      } else {
        // No match found, but we should use a default option
        this.handleDefaultCondition(controlSet, value);
      }
    } catch (error) {
      console.error('Error processing conditional control set:', error);
    }
  }

  /**
   * Handles the case where a matching condition is found
   */
  private handleMatchingCondition(
    controlSet: ConditionalControlSet,
    matchingOption: ConditionalControlOption,
    matchingValue: any
  ): void {
    this.removeExistingConditionControls(controlSet, matchingOption);

    const controlKey = matchingOption.key;

    // Check if control already exists before creating a new one

    if (!this.hasKey(controlKey)) {
      const control = matchingOption.factory(matchingValue);
      this.addControl(controlKey, control);
    }
  }

  /**
   * Handles the case where no matching condition is found and a default control should be used
   */
  private handleDefaultCondition(
    controlSet: ConditionalControlSet,

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    value: { [key: string]: any }
  ): void {
    const defaultOption = controlSet.conditions[0];
    const controlKey = defaultOption.key;

    this.removeExistingConditionControls(controlSet, defaultOption);

    if (this.hasKey(controlKey)) {
      this.resetExistingDefaultControl(defaultOption, controlKey);
    } else {
      const control = defaultOption.factory();
      this.addControl(controlKey, control);
    }
  }

  /**
   * Resets an existing default control to its initial state
   */
  private resetExistingDefaultControl(
    defaultOption: ConditionalControlOption,
    controlKey: string
  ): void {
    const tempControl = defaultOption.factory();
    const defaultValue = tempControl.value;

    this.updateExistingControl(controlKey, defaultValue);
  }

  /**
   * Removes all controls associated with a conditional control set
   */
  private removeConditionalControls(controlSet: ConditionalControlSet): void {
    const allConditionKeys = controlSet.conditions.map(
      condition => condition.key
    );

    for (const key of allConditionKeys) {
      if (this.hasKey(key)) {
        this.removeControl(key);
      }
    }
  }

  /**
   * Helper method to check if this form group hasKey a control with the given name
   * @param controlName The name of the control to check for
   * @returns True if the control exists, false otherwise
   */
  hasKey(controlName: string): boolean {
    return (
      (this.controls as Record<string, AbstractControl>)[controlName] !==
      undefined
    );
  }

  /**
   * Override reset to ensure error recalculation happens after form is reset
   */
  override reset(
    value?: any,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    }
  ): void {
    super.reset(value, options);

    if (options?.emitEvent) {
      this.allErrors = this.getAllErrors();
    }
  }

  /**
   * Removes any existing controls that were created by other conditions in the same set
   * @param controlSet The control set being processed
   * @param currentCondition The current condition that matched (to exclude from removal)
   */
  private removeExistingConditionControls(
    controlSet: ConditionalControlSet,
    currentCondition: ConditionalControlOption
  ): void {
    const otherKeys = controlSet.conditions
      .filter(condition => condition !== currentCondition)
      .map(condition => condition.key);

    for (const key of otherKeys) {
      if (this.hasKey(key)) {
        this.removeControl(key);
      }
    }
  }

  /**
   * Finds a matching option in a control set for the given key
   * @param controlSet The control set to search
   * @param key The key to match
   * @returns The matching option or undefined
   */
  private findMatchingOptionByKey(
    controlSet: ConditionalControlSet,
    key: string
  ): ConditionalControlOption | undefined {
    for (const condition of controlSet.conditions) {
      if (condition.key === key) {
        return condition;
      }
    }
    return undefined;
  }

  /**
   * Updates an existing control with a new value
   * @param controlKey The key of the control to update
   * @param value The value to set
   * @returns True if the control was updated, false otherwise
   */
  private updateExistingControl(controlKey: string, value?: any): void {
    if (value === undefined) {
      return;
    }

    const existingControl = this.get(controlKey);
    if (existingControl) {
      setValueToControl(existingControl, value);
    }
  }

  /**
   * Creates a new control for the given option and adds it to the form
   * @param controlSet The control set containing the option
   * @param option The option to create a control for
   * @param value The value to set
   * @returns True if successful
   */
  private createConditionalControl(
    controlSet: ConditionalControlSet,
    option: ConditionalControlOption,
    value?: any
  ): void {
    this.removeExistingConditionControls(controlSet, option);

    const control = option.factory(value);
    this.addControl(option.key, control);

    processNestedControls(control, value || control.value);

    if (value !== undefined) {
      this.patchValue(value);
    }
  }

  /**
   * Selects a conditional control based on the key and updates it with the value
   * @param key The key of the control to select
   * @param value The value to set
   * @returns True if a control was selected or created, false otherwise
   */
  public selectConditionalControl(key: string, value?: any): boolean {
    if (!this.conditionalControls) {
      return false;
    }

    for (const controlSet of this.conditionalControls) {
      const matchingOption = this.findMatchingOptionByKey(controlSet, key);

      if (!matchingOption) {
        continue;
      }

      const controlKey = matchingOption.key;

      if (this.hasKey(controlKey)) {
        // Control already exists — remove siblings and update in place, no need to recreate
        this.removeExistingConditionControls(controlSet, matchingOption);
        this.updateExistingControl(controlKey, value);
      } else {
        // Control doesn't exist yet — create it fresh (also removes siblings internally)
        this.createConditionalControl(controlSet, matchingOption, value);
      }

      return true;
    }
    return false;
  }

  public getAllErrors() {
    // The group's own errors (cross-field validators) come first, then every
    // descendant's.
    return [
      ...getControlOwnErrors(this),
      ...getFormValidationErrors(this.controls),
    ].map(error => ({
      ...error,
      message: this._getErrorMessage(error),
    }));
  }

  public getChildErrors(controllerName: string) {
    const controller = this.get(controllerName);
    if (!controller) return [];

    const controller_name = getControlName(controller);
    let errors: FieldValidationErrorObject[] = [];

    if (controller instanceof FormControl) {
      errors = getFieldValidationErrors(controller, controller_name);
    } else if (controller instanceof FormGroup) {
      errors = getFormValidationErrors(controller.controls);
    } else if (controller instanceof FormArray) {
      for (const [index, e] of controller.controls.entries()) {
        if (e instanceof FormGroup) {
          errors = errors.concat(getFormValidationErrors(e.controls, index));
        } else {
          errors = getFieldValidationErrors(e as FormControl, controller_name);
        }
      }
    }

    return errors.map(error => ({
      ...error,
      message: this._getErrorMessage(error),
    }));
  }

  private _getAncestorsErrorMessages(
    controller: AbstractControl
  ): ReadonlyArray<ErrorMessageObject> {
    const errorMessages: ErrorMessageObject[] = [];
    if (controller.parent !== null) {
      if (
        controller.parent instanceof CustomFormGroup &&
        controller.parent.errorMessages
      ) {
        errorMessages.push(...controller.parent.errorMessages);
      }
      errorMessages.push(...this._getAncestorsErrorMessages(controller.parent));
    }
    return errorMessages;
  }

  private _getErrorMessage(error: FieldValidationErrorObject) {
    const findMessage = (
      messages: ReadonlyArray<ErrorMessageObject> | undefined
    ) =>
      messages?.find(
        message =>
          message.fieldKey === error.control_name &&
          message.errorName === error.error_name
      );

    const messageObject =
      findMessage(this.errorMessages) ||
      findMessage(this._getAncestorsErrorMessages(this));

    if (messageObject) {
      return typeof messageObject.message === 'function'
        ? messageObject.message(error)
        : messageObject.message;
    }
    return '';
  }

  /**
   * Refreshes the value changes subscription.
   * Useful after form reset or when changing validators dynamically.
   */
  public refreshValueChangesSubscription(): void {
    this.setupValueChangesSubscription();
  }
}
